import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import downloadCsv from "../../../global/utils/downloadCsv";
import { DeviceColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { DeviceFilter } from "../Components/FilterComponent";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { hasRole, getUserData } from "../../../global/hooks/getUserRole";
import CommonButton from "../../../global/components/CommonButton";
import { RiExportFill } from "react-icons/ri";
import { DeviceActReportForm } from "../Components/DeviceActReportForm";
import { FaFileAlt } from "react-icons/fa";

const initialFilterState = {
  DeviceNo: "",
  DeviceName: "",
  Mac: "",
  Product: null,
  Model: null,
  SiteType: null,
  SiteTypeName: "",
  NodeType: null,
  NodeTypeName: "",
  OemNo: "",
  // ProductionNo: "",
  DealerNo: "",
  CustomerNo: "",
  Online: null,
};

const getDeviceTableExportQueryString = (searchParams) => {
  const q = new URLSearchParams(searchParams);
  q.delete("pageSize");
  q.delete("total");
  q.delete("current");
  q.delete("limit");
  return q.toString();
};

export const Device = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "DeviceNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //
  const [filter, setFilter] = useState(initialFilterState);
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [columnsValues, setColumnsValues] = useState([]);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [activationReportModal, setActivationReportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Device",
      href: "/device",
    },
  ];

  // to get table data from API server
  const query = new URLSearchParams(params);

  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();

  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/devices/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/devices/`;
  const { data, loading, error, refetch } = useGet(url);

  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/devices/bulk-delete/`;

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    // deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  const deviceListExportQs = getDeviceTableExportQueryString(params);

  const activationReportUrl = deviceListExportQs
    ? `${process.env.REACT_APP_API_URL}/api/devices/activation-report/?${deviceListExportQs}`
    : `${process.env.REACT_APP_API_URL}/api/devices/activation-report/`;

  const deviceExportUrl = deviceListExportQs
    ? `${process.env.REACT_APP_API_URL}/api/devices/device-export/?${deviceListExportQs}`
    : `${process.env.REACT_APP_API_URL}/api/devices/device-export/`;

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  }; //
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record) => {
    return {
      onClick: (event) => {
        navigate(`/device/info/${record?.DeviceNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "DeviceNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //

    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "DeviceNo", // Make sure to specify sorting if necessary
      order: "desc",
    };

    // Update the parameters in the state
    setParams(params);
    refetch();
    setSelectedRowKeys([]);
    setResetTrigger(!resetTrigger);
  };

  const onDeleteConfirm = async () => {
    try {
      // if (selectedRowKeys.length === 1) {
      //   await deleteData();
      // } else {
      await deleteMultiple({ ids: selectedRowKeys });
      // }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.DeviceNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  const handleDeviceExport = async () => {
    const token = getUserData();
    const access_token = token ? token.access_token : null;
    try {
      setExportLoading(true);
      const response = await axios.get(deviceExportUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
        responseType: "text",
      });
      const payload = response.data;
      if (
        typeof payload === "object" &&
        payload !== null &&
        payload.message === "No devices found."
      ) {
        openErrorNotification("No Data Found");
        return;
      }
      if (payload != null && String(payload).trim() !== "") {
        downloadCsv(
          payload,
          window.location,
          `Device Export (${dayjs().format("YYYY-MM-DD")})`,
        );
        openConfirmNotification("Success");
      }
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message || "Something went wrong.",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const buttonData = [
    {
      label: "Export",
      icon: <RiExportFill className="!text-[14px] font-medium" />,
      onClick: handleDeviceExport,
      loading: exportLoading,
    },
    ...(hasRole(1, 2, 3)
      ? [
          {
            label: "Activation Report",
            icon: <FaFileAlt className="!text-[14px] font-medium" />,
            onClick: () => {
              setActivationReportModal(true);
            },
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonData} />
        </CommonBreadcrumb>
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <DeviceFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setShowModal={setShowModal}
          selectedRowKeys={selectedRowKeys}
          setFilter={setFilter}
          data={data}
          setColumnsValues={setColumnsValues}
        />
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <div className="mt-4">
          <GlobalTable
            columns={DeviceColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              columnsValues,
            )}
            dataSource={listData}
            rowSelection={hasRole(1, 2) ? rowSelection : null}
            pagination={paginationConfig}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
            resize
            resetTrigger={resetTrigger}
          />
        </div>
      </div>
      {activationReportModal && (
        <DeviceActReportForm
          url={activationReportUrl}
          modalOpen={activationReportModal}
          setModalOpen={setActivationReportModal}
        />
      )}
    </div>
  );
};
